import documentsSvc from '@efex-services/documents/documentsSvc';
import { OnboardingDocumentTypeIds } from '@efex-services/documents/types';
import useImmediateApiCall from '@main-hooks/useImmediateApiCall';
import { selectBusiness } from '@pages/profile/store/selectors';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useApiCall } from 'use-api-call';
import { UseFileServiceReturn } from './types/useFileService.type';

const useFileService = (): UseFileServiceReturn => {
  const company = useSelector(selectBusiness);

  const {
    loading: loadingOnboardingDocuments,
    data: onboardingDocuments,
    retry: getOnboardingDocuments,
  } = useImmediateApiCall(() =>
    documentsSvc.getOnboardingDocuments(Number(company?.enterpriseId))
  );

  const {
    loading: uploadingDocuments,
    data: uploadData,
    error: errorUploadDocuments,
    invoke: uploadDocuments,
  } = useApiCall(
    ({
      currentDocuments,
      name,
    }: {
      currentDocuments: File[];
      name: OnboardingDocumentTypeIds;
    }) =>
      documentsSvc.uploadOnboardingDocument(
        company?.enterpriseId as unknown as number,
        {
          documentTypeId: OnboardingDocumentTypeIds[
            name
          ] as unknown as OnboardingDocumentTypeIds,
          totalDocuments: currentDocuments.length,
          documents: currentDocuments as unknown as FormData,
        }
      )
  );

  const {
    loading: loadingDelete,
    data: dataDelete,
    error: errorDelete,
    invoke: deleteDocument,
  } = useApiCall(({ documentId }: { documentId: number }) =>
    documentsSvc.deleteOnboardingDocuments(
      company?.enterpriseId as unknown as number,
      documentId
    )
  );

  const uploadFilesFromEvent = (files: FileList, name: string) => {
    const filesArray: File[] = [];
    for (let index = 0; index <= files.length - 1; index += 1) {
      filesArray[index] = files.item(index) as unknown as File;
    }
    uploadDocuments({
      currentDocuments: filesArray,
      name,
    });
  };

  const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files, name } = event.target;
    if (files && files.length) {
      uploadFilesFromEvent(files, name);
    }
  };

  const handleDropChangeFile = (files: FileList, name: string) => {
    if (files && files.length) {
      uploadFilesFromEvent(files, name);
    }
  };

  const handleDeleteFile = (id: string | number) => {
    deleteDocument({ documentId: id });
  };

  useEffect(() => {
    if (uploadData && !errorUploadDocuments && !uploadingDocuments) {
      getOnboardingDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadData, errorUploadDocuments, uploadingDocuments]);

  useEffect(() => {
    if (dataDelete && !errorDelete && !loadingDelete) {
      getOnboardingDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataDelete, errorDelete, loadingDelete]);

  return {
    loadingOnboardingDocuments,
    errorUploadDocuments,
    onboardingDocuments,
    getOnboardingDocuments,
    handleChangeFile,
    handleDropChangeFile,
    handleDeleteFile,
  };
};

export default useFileService;
